import React, { useState, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  FiDollarSign,
  FiCheckCircle,
  FiLayers,
  FiTrendingUp,
  FiAlertTriangle,
  FiClock,
} from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import StatusChip from "../../../Common/StatusChip";
import DateRangeFilter from "../../../../Common/DateFilter";
import ReportCard from "../../../Common/ReportCard";
import { getContractBillingAbstractApi } from "../../../../Api/ClientWork/ClientWork";
import { useReportData } from "./useReportData";

const ContractBillingAbstractReport = () => {
  const gridRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const data = useReportData(getContractBillingAbstractApi, dateRange);
  const [columnVisibility, setColumnVisibility] = useState({});

  // Summary KPI calculations
  const kpis = useMemo(() => {
    const totalContractValue = data.reduce((s, r) => s + (r.contractValue || 0), 0);
    const totalGrossCertified = data.reduce((s, r) => s + (r.cumulativeGross || 0), 0);
    const totalNetInvoiced = data.reduce((s, r) => s + (r.netInvoiced || 0), 0);
    const totalCashCollected = data.reduce((s, r) => s + (r.cashReceived || 0), 0);
    const totalOutstanding = data.reduce((s, r) => s + (r.outstandingReceivable || 0), 0);
    const totalRemaining = data.reduce((s, r) => s + (r.remainingUnbilled || 0), 0);
    const avgCompletion =
      totalContractValue > 0 ? ((totalGrossCertified / totalContractValue) * 100).toFixed(1) : 0;

    return {
      totalContractValue,
      totalGrossCertified,
      totalNetInvoiced,
      totalCashCollected,
      totalOutstanding,
      totalRemaining,
      avgCompletion,
    };
  }, [data]);

  // Column definitions for Abstract of Bills
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Project / Site Name",
        field: "projectName",
        flex: 1.8,
        minWidth: 200,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 600, color: "var(--color-primary, #0b6462)" }}>{p.value}</span>
        ),
      },
      {
        headerName: "Project Owner (Client)",
        field: "clientName",
        flex: 2,
        minWidth: 220,
      },
      {
        headerName: "Original Contract Value (PKR)",
        field: "contractValue",
        flex: 1.5,
        minWidth: 160,
        valueFormatter: (p) => (p.value ? `PKR ${Number(p.value).toLocaleString()}` : "—"),
      },
      {
        headerName: "Cumulative Certified (Gross)",
        field: "cumulativeGross",
        flex: 1.5,
        minWidth: 160,
        valueFormatter: (p) => (p.value ? `PKR ${Number(p.value).toLocaleString()}` : "—"),
      },
      {
        headerName: "% Financial Completion",
        field: "financialCompletionPct",
        flex: 1.4,
        minWidth: 150,
        cellRenderer: (p) => {
          const pct = Number(p.value || 0);
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div
                style={{
                  width: "50px",
                  height: "7px",
                  borderRadius: "4px",
                  background: "#e2e8f0",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${Math.min(pct, 100)}%`,
                    height: "100%",
                    background: pct >= 100 ? "#16a34a" : "var(--color-primary, #0b6462)",
                  }}
                />
              </div>
              <span style={{ fontWeight: 600, fontSize: "12px" }}>{pct}%</span>
            </div>
          );
        },
      },
      {
        headerName: "Total Deductions",
        field: "totalDeductions",
        flex: 1.4,
        minWidth: 150,
        valueFormatter: (p) => (p.value ? `PKR ${Number(p.value).toLocaleString()}` : "—"),
      },
      {
        headerName: "Net Invoiced to Date",
        field: "netInvoiced",
        flex: 1.5,
        minWidth: 160,
        valueFormatter: (p) => (p.value ? `PKR ${Number(p.value).toLocaleString()}` : "—"),
      },
      {
        headerName: "Total Cash Received",
        field: "cashReceived",
        flex: 1.5,
        minWidth: 160,
        valueFormatter: (p) => (p.value ? `PKR ${Number(p.value).toLocaleString()}` : "—"),
      },
      {
        headerName: "Current Outstanding",
        field: "outstandingReceivable",
        flex: 1.5,
        minWidth: 160,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 600, color: Number(p.value) > 0 ? "#dc2626" : "#16a34a" }}>
            {p.value ? `PKR ${Number(p.value).toLocaleString()}` : "—"}
          </span>
        ),
      },
      {
        headerName: "Remaining Unbilled Value",
        field: "remainingUnbilled",
        flex: 1.5,
        minWidth: 160,
        valueFormatter: (p) => (p.value ? `PKR ${Number(p.value).toLocaleString()}` : "—"),
      },
      {
        headerName: "Status",
        field: "status",
        flex: 1,
        minWidth: 110,
        cellRenderer: (p) => (
          <StatusChip status={p.value} type={p.value === "Completed" ? "success" : "info"} />
        ),
      },
    ];
  }, []);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
    }),
    [],
  );

  // Filtered dataset
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const q = searchTerm.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (item.projectName && item.projectName.toLowerCase().includes(q)) ||
        (item.clientName && item.clientName.toLowerCase().includes(q));

      const matchesStatus =
        statusFilter === "all" || item.status?.toLowerCase() === statusFilter.toLowerCase();

      return matchesSearch && matchesStatus;
    });
  }, [data, searchTerm, statusFilter]);

  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback((field) => {
    if (!gridRef.current?.columnApi) {
      return;
    }
    const col = gridRef.current.columnApi.getColumn(field);
    if (col) {
      const isVisible = col.isVisible();
      gridRef.current.columnApi.setColumnVisible(field, !isVisible);
      setColumnVisibility((prev) => ({ ...prev, [field]: !isVisible }));
    }
  }, []);

  const handleExportCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `Client_Contract_Billing_Abstract_${new Date().toISOString().slice(0, 10)}.csv`,
      });
      toast.success("Billing abstract report exported to CSV");
    }
  };

  return (
    <div className="pt-2 app_font">
      {/* ── Summary Cards ── */}
      <div className="row g-2 mb-1 mt-1">
        <div className="col-lg-2 col-md-4 col-sm-6">
          <ReportCard
            title="TOTAL AWARDS"
            value={`PKR ${(kpis.totalContractValue / 1000000).toFixed(1)}M`}
            subtitle="Contract portfolio"
            icon={FiDollarSign}
            color="#0b6462"
          />
        </div>
        <div className="col-lg-2 col-md-4 col-sm-6">
          <ReportCard
            title="CERTIFIED (GROSS)"
            value={`PKR ${(kpis.totalGrossCertified / 1000000).toFixed(1)}M`}
            subtitle={`${kpis.avgCompletion}% billed`}
            icon={FiCheckCircle}
            color="#0284c7"
          />
        </div>
        <div className="col-lg-2 col-md-4 col-sm-6">
          <ReportCard
            title="NET INVOICED"
            value={`PKR ${(kpis.totalNetInvoiced / 1000000).toFixed(1)}M`}
            subtitle="Post deductions"
            icon={FiLayers}
            color="#0d9488"
          />
        </div>
        <div className="col-lg-2 col-md-4 col-sm-6">
          <ReportCard
            title="CASH COLLECTED"
            value={`PKR ${(kpis.totalCashCollected / 1000000).toFixed(1)}M`}
            subtitle="Realized in bank"
            icon={FiTrendingUp}
            color="#16a34a"
          />
        </div>
        <div className="col-lg-2 col-md-4 col-sm-6">
          <ReportCard
            title="OUTSTANDING AR"
            value={`PKR ${(kpis.totalOutstanding / 1000000).toFixed(1)}M`}
            subtitle="Unpaid certified bills"
            icon={FiAlertTriangle}
            color="#dc2626"
          />
        </div>
        <div className="col-lg-2 col-md-4 col-sm-6">
          <ReportCard
            title="UNBILLED BACKLOG"
            value={`PKR ${(kpis.totalRemaining / 1000000).toFixed(1)}M`}
            subtitle="Remaining contract work"
            icon={FiClock}
            color="#8b5cf6"
          />
        </div>
      </div>

      {/* ── Filter Toolbar (General Journal style) ── */}
      <div className="row app_font">
        <div className="col-12 pt-1 p-0">
          <div
            className="d-flex flex-wrap align-items-center justify-content-end pt-2 pb-2 app_font w-100"
            style={{ gap: "12px", width: "100%" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                minHeight: "30px",
                maxWidth: "100%",
                marginRight: "auto",
                padding: "4px 12px",
                color: "var(--btn-comp-text, #0b6462)",
                fontSize: "var(--font-md)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="Billing Abstract Report"
            >
              Billing Abstract Report
            </div>
            <SearchFilter
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search project, client..."
            />

            <div className="d-flex align-items-center" style={{ maxWidth: "260px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <DateRangeFilter
                  initialFrom={dateRange.from}
                  initialTo={dateRange.to}
                  onChange={(val) => setDateRange(val)}
                />
                <label>Date</label>
              </div>
            </div>

            <div className="d-flex align-items-center" style={{ maxWidth: "200px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                </select>
                <label>Status</label>
              </div>
            </div>

            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* ── AG Grid Table ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div
            className="ag-theme-alpine vouchers_table_height3 mt-1"
            style={{ width: "100%", position: "relative" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowStyle={{ cursor: "pointer" }}
            />
            {/* Table Footer Actions */}
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "15px",
                display: "flex",
                gap: "10px",
                zIndex: 1,
              }}
            >
              <ActionButton
                text="Download CSV"
                isPrint={true}
                newBtn="true"
                onClick={handleExportCSV}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractBillingAbstractReport;
